import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex justify-between items-center bg-gray-200 p-2 mb-s rounded" }
const _hoisted_2 = { class: "flex justify-between" }
const _hoisted_3 = { class: "flex justify-between flex-col" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = {
  key: 0,
  class: "flex items-center justify-center"
}

import IconButton from "@/components/IconButton.vue";
import SmallTag from "@/components/SmallTag.vue";
import { ref, computed } from "vue";
import { formatDate } from "@/helpers/datehelper";
import { useOrdersStore } from "@/store/ordersStore";
import RoundedCard from "@/components/RoundedCard.vue";
import { BoxWithOrders, Message } from "@/interfaces/message";


export default /*#__PURE__*/_defineComponent({
  __name: 'OrderCalendar',
  emits: ["selectDate"],
  setup(__props, { emit: __emit }) {

const boxes = useOrdersStore().organizedBoxes;

const month = ref<number>(new Date().getMonth() + 1);
const year = ref<number>(new Date().getFullYear());

const daysOfWeek: string[] = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const emit = __emit;
const getMonthDays = (month: number, year: number): number => {
  return new Date(year, month, 0).getDate();
};

const getFirstDayIndex = (month: number, year: number): number => {
  return new Date(year, month - 1, 1).getDay();
};

function boxesAtDay(day: number): {
  color: "orange" | "green";
  amount: number;
} {
  const date = formatDate(new Date(year.value, month.value - 1, day));
  const boxesAtDay = boxes.filter((box) => box.due === date);
  const undelieveredBoxesAtDay = boxesAtDay.filter((box) => !delivered(box));
  return {
    amount:
      undelieveredBoxesAtDay.length > 0
        ? undelieveredBoxesAtDay.length
        : boxesAtDay.length,
    color: undelieveredBoxesAtDay.length > 0 ? "orange" : "green",
  };
}

function delivered(box: Message): boolean {
  return [2, 3].includes(box.medication_box?.status ?? 0);
}

const generateCalendar = (): number[][] => {
  const monthDays = getMonthDays(month.value, year.value);
  const firstDayIndex = getFirstDayIndex(month.value, year.value);
  const calendar: number[][] = [];

  let dayCounter = 1;
  for (let i = 0; i < 5; i++) {
    const week: number[] = [];
    for (let j = 0; j < 7; j++) {
      if (i === 0 && j < firstDayIndex - 1) {
        week.push(0);
      } else if (dayCounter <= monthDays) {
        week.push(dayCounter);
        dayCounter++;
      } else {
        week.push(0);
      }
    }
    calendar.push(week);
  }
  return calendar;
};

const calendar = ref<number[][]>(generateCalendar());

const monthName = computed(() => {
  const months = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ];
  return months[month.value - 1];
});

const isCurrentDay = (day: number): boolean => {
  const currentDate = new Date();
  return (
    currentDate.getFullYear() === year.value &&
    currentDate.getMonth() + 1 === month.value &&
    day === currentDate.getDate()
  );
};

const handleDayClick = (day: number): void => {
  const selectedDate = new Date(year.value, month.value - 1, day);
  emit("selectDate", selectedDate);
};

const prevMonth = (): void => {
  if (month.value === 1) {
    month.value = 12;
    year.value--;
  } else {
    month.value--;
  }
  calendar.value = generateCalendar();
};

const nextMonth = (): void => {
  if (month.value === 12) {
    month.value = 1;
    year.value++;
  } else {
    month.value++;
  }
  calendar.value = generateCalendar();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(RoundedCard, { class: "mt-m mb-s" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(IconButton, {
          icon: "left",
          onClick: prevMonth
        }),
        _createElementVNode("h2", null, _toDisplayString(monthName.value) + " " + _toDisplayString(year.value), 1),
        _createVNode(IconButton, {
          icon: "right",
          onClick: nextMonth
        })
      ]),
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(daysOfWeek, (day, index) => {
          return _createElementVNode("div", {
            key: index,
            class: "text-center text-gray-600 font-bold w-full"
          }, _toDisplayString(day), 1)
        }), 64))
      ]),
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendar.value, (week, weekIndex) => {
          return (_openBlock(), _createElementBlock("div", {
            key: weekIndex,
            class: "flex justify-between"
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(week, (day, dayIndex) => {
              return (_openBlock(), _createElementBlock("div", {
                key: dayIndex,
                class: _normalizeClass([{ 'bg-gray-200': isCurrentDay(day) }, "flex flex-col h-xxl items-center justify-center cursor-pointer w-full"]),
                onClick: ($event: any) => (handleDayClick(day)),
                draggable: false,
                onDragover: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"]))
              }, [
                _createElementVNode("div", null, _toDisplayString(day || ""), 1),
                (boxesAtDay(day).amount > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createVNode(SmallTag, {
                        small: "",
                        color: boxesAtDay(day).color
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(boxesAtDay(day).amount), 1)
                        ]),
                        _: 2
                      }, 1032, ["color"])
                    ]))
                  : _createCommentVNode("", true)
              ], 42, _hoisted_4))
            }), 128))
          ]))
        }), 128))
      ])
    ]),
    _: 1
  }))
}
}

})